import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  FormInstance,
  Select,
} from 'antd';
import { useQuery } from '@common/hooks';
import { WorksUsersSelect } from 'works/legacy/components/form-fields/works-users-select';
import {
  CurrencyInput,
  DatePicker,
  Gutter,
  InfoTooltip,
  TextArea,
} from '@common/components';
import { EffectiveBillingCardQuery } from 'works/legacy/graphql/effective-billing-card';
import { toYearMonthDay } from 'works/legacy/lib/date-helper';
import {
  getBillingCardRateByShiftType,
  RatesByShiftType,
} from '@institutions/services';
import { ShiftTypesRadioInput } from '@jobs/components';
import { StaffingRequestType } from '@jobs/graphql/queries/staffing-request-query';
import { iterationMap } from 'works/legacy/services/iteration-service';
import { EditRatesEstimatedCostTable } from './_edit-rates-estimated-cost-table';

type WorksUserType = {
  id: string;
  name: string;
};

type EditStaffingRequestFormPartOneProps = {
  staffingRequest: StaffingRequestType;
  visible: boolean;
  form: FormInstance;
  setIncreasedEstimatedCost: (value: boolean) => void;
  deviated: boolean;
  setIsDefaultRate: (value: boolean) => void;
  isDefaultRate: boolean;
  canUpdateDetails: boolean;
  enableOnlyOpenPositionsField?: boolean;
};

export function EditStaffingRequestFormPartOne({
  staffingRequest,
  visible,
  form,
  setIncreasedEstimatedCost,
  deviated,
  setIsDefaultRate,
  isDefaultRate,
  canUpdateDetails,
  enableOnlyOpenPositionsField,
}: EditStaffingRequestFormPartOneProps) {
  const isOnDemand = staffingRequest.staffingRequestType === 'on_demand';
  const [shiftType, setShiftType] = useState(staffingRequest.shiftType);
  const [startDate, setStartDate] = useState(staffingRequest.startDate);

  const [billingCardRates, setBillingCardRates] = useState<RatesByShiftType>({
    travelerRate: 0,
    localRate: 0,
    directSourcingRate: 0,
  });
  const [estimatedCosts, setEstimatedCosts] = useState({
    templateEstimatedCost: 0,
    travelerEstimatedCost: 0,
    localEstimatedCost: 0,
    directSourcingEstimatedCost: 0,
    fieldRateEstimatedCost: 0,
  });

  const worksUsers: WorksUserType[] =
    staffingRequest.organizationUnit.department.activeNonInternalWorksMemberships.map(
      (membership) => membership.worksUser
    );
  const canShowRateDetails = staffingRequest.canShowRateDetails.value;
  const canUpdateRateAffectedDetails =
    staffingRequest.canUpdateRateAffectedDetails.value;

  const { data: billingCardData } = useQuery(EffectiveBillingCardQuery, {
    variables: {
      effectiveDate: toYearMonthDay(startDate),
      organizationUnitId: staffingRequest.organizationUnit.id,
      roleId: staffingRequest.role.id,
      roleLevel: staffingRequest.roleLevel,
    },
  });

  const effectiveBillingCard = billingCardData?.effectiveBillingCard;

  useEffect(() => {
    if (!visible) {
      setShiftType(staffingRequest.shiftType);
      setStartDate(staffingRequest.startDate);
    }
  }, [visible, staffingRequest]);

  useEffect(() => {
    const newBillingCardRates = getBillingCardRateByShiftType(
      effectiveBillingCard,
      shiftType
    );

    if (newBillingCardRates) {
      setBillingCardRates(newBillingCardRates);
      onEstimatedCostChange(newBillingCardRates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingCardData, shiftType]);

  const onEstimatedCostChange = (billingCardRate: RatesByShiftType) => {
    const fields = form.getFieldsValue();
    const fieldRate = fields.rate;
    const positions =
      fields.openPositions +
      staffingRequest.filledStaffingRequestPositionsCount;
    const baseCost =
      fields.shiftsPerWeek *
      fields.hoursPerShift *
      fields.lengthInWeeks *
      positions;

    const fieldRateEstimatedCost = baseCost * fieldRate;
    const templateEstimatedCost =
      baseCost * staffingRequest.staffingRequestTemplate.billingRegularRate;
    const travelerEstimatedCost = baseCost * billingCardRate.travelerRate;
    const localEstimatedCost = baseCost * billingCardRate.localRate;
    const directSourcingEstimatedCost =
      baseCost * billingCardRate.directSourcingRate;

    setEstimatedCosts({
      templateEstimatedCost: templateEstimatedCost,
      travelerEstimatedCost: travelerEstimatedCost,
      localEstimatedCost: localEstimatedCost,
      directSourcingEstimatedCost: directSourcingEstimatedCost,
      fieldRateEstimatedCost: fieldRateEstimatedCost,
    });

    const maxEstimatedCost = Math.max(
      fieldRateEstimatedCost,
      templateEstimatedCost,
      travelerEstimatedCost,
      localEstimatedCost,
      directSourcingEstimatedCost
    );

    setIncreasedEstimatedCost(maxEstimatedCost > staffingRequest.estimatedCost);
  };

  const openPositionsOptions = useMemo(() => {
    if (!enableOnlyOpenPositionsField) {
      return [];
    }

    const openPositions = form.getFieldValue('openPositions');

    return iterationMap(openPositions, (index) => ({
      label: `${index + 1}`,
      value: index + 1,
    }));
  }, [form, enableOnlyOpenPositionsField]);

  const hasRateFieldChanged =
    staffingRequest.billingRegularRate !== form.getFieldValue('rate');

  const handleRateOnRestore = hasRateFieldChanged
    ? () => {
        setIsDefaultRate(true);
        form.setFieldsValue({
          rate: staffingRequest.billingRegularRate,
        });
        onEstimatedCostChange(billingCardRates);
      }
    : undefined;

  return (
    <>
      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item
            name="startDate"
            label={
              <>
                Start Date
                <InfoTooltip
                  size="md"
                  leftOffset="sm"
                  title="Changing the Start Date may affect the rate based on the rate card."
                />
              </>
            }
            rules={[{ required: true, message: 'Please enter a start date' }]}
            hidden={!visible}
          >
            <DatePicker
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              size="large"
              onChange={(_, date) => {
                setStartDate(date);
              }}
              disabled={
                !canUpdateRateAffectedDetails || enableOnlyOpenPositionsField
              }
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="lengthInWeeks"
            label="Length in Weeks"
            rules={[
              {
                required: true,
                message: 'Please specify length in weeks',
              },
              {
                type: 'integer',
                min: 1,
                max: 199,
              },
            ]}
            hidden={!visible}
          >
            <InputNumber
              size="large"
              placeholder="0"
              required={true}
              onChange={() => {
                onEstimatedCostChange(billingCardRates);
              }}
              disabled={
                !canUpdateRateAffectedDetails || enableOnlyOpenPositionsField
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="international" hidden={!visible}>
        <Checkbox
          defaultChecked={staffingRequest.international}
          onChange={({ target }) =>
            form.setFieldsValue({ international: target.checked })
          }
          disabled={enableOnlyOpenPositionsField}
        >
          International Candidate
        </Checkbox>
      </Form.Item>

      <ShiftTypesRadioInput
        onChange={(event) => {
          setShiftType(event.target.value);
        }}
        tooltipMessage="Changing the Shift may affect the rate based on the rate card."
        disabled={!canUpdateRateAffectedDetails || enableOnlyOpenPositionsField}
        hidden={!visible}
      />

      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item
            name="shiftsPerWeek"
            label="Shifts Per Week"
            rules={
              isOnDemand
                ? []
                : [
                    {
                      required: true,
                      message: 'Please enter a number',
                    },
                    {
                      type: 'integer', // disable this validation also when is on demand Staffing Request
                      min: 1,
                      max: 99,
                    },
                  ]
            }
            hidden={!visible}
          >
            <InputNumber
              size="large"
              placeholder="0"
              required={!isOnDemand}
              onChange={() => {
                onEstimatedCostChange(billingCardRates);
              }}
              disabled={
                isOnDemand ||
                !canUpdateRateAffectedDetails ||
                enableOnlyOpenPositionsField
              }
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="hoursPerShift"
            label="Hours Per Shift"
            rules={
              isOnDemand
                ? []
                : [
                    {
                      required: true,
                      message: 'Please enter a number',
                    },
                    {
                      type: 'integer',
                      min: 1,
                      max: 99,
                    },
                  ]
            }
            hidden={!visible}
          >
            <InputNumber
              size="large"
              placeholder="0"
              required={!isOnDemand}
              onChange={() => {
                onEstimatedCostChange(billingCardRates);
              }}
              disabled={
                isOnDemand ||
                !canUpdateRateAffectedDetails ||
                enableOnlyOpenPositionsField
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item
            name="rate"
            label="Rate"
            rules={[{ required: true, message: 'Please enter a rate' }]}
            hidden={!visible || !canShowRateDetails}
          >
            <CurrencyInput
              disabled={
                !canUpdateRateAffectedDetails || enableOnlyOpenPositionsField
              }
              size="large"
              actionKind="restore"
              onAction={handleRateOnRestore}
              onChange={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                onEstimatedCostChange(billingCardRates);
                setIsDefaultRate(
                  numValue === staffingRequest.billingRegularRate
                );
              }}
            />
          </Form.Item>
        </Col>

        <Col span={canShowRateDetails ? 12 : 24}>
          <Form.Item
            name="openPositions"
            label="Open positions"
            rules={[
              {
                required: true,
                message: 'Enter the number of open positions',
              },
              {
                type: 'integer',
                min: 1,
                max: 99,
              },
            ]}
            hidden={!visible}
          >
            {enableOnlyOpenPositionsField ? (
              <Select
                size="large"
                placeholder="0"
                options={openPositionsOptions}
                onChange={() => {
                  onEstimatedCostChange(billingCardRates);
                }}
              />
            ) : (
              <InputNumber
                size="large"
                placeholder="0"
                onChange={() => {
                  onEstimatedCostChange(billingCardRates);
                }}
                disabled={!canUpdateRateAffectedDetails}
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      {visible && !isDefaultRate && (
        <Alert
          message="By adjusting this rate, you're unlinking it from the rate card. This Staffing Request will not follow future rate card updates."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {visible && canShowRateDetails && (
        <Gutter direction="bottom" size="md">
          <EditRatesEstimatedCostTable
            form={form}
            estimatedCosts={estimatedCosts}
            effectiveBillingCard={effectiveBillingCard}
            staffingRequest={staffingRequest}
            billingCardRates={billingCardRates}
            deviated={deviated}
          />
        </Gutter>
      )}

      <Form.Item
        label="Hiring Leader"
        name="hiringLeaderId"
        rules={[{ required: true, message: 'Please select the hiring leader' }]}
        hidden={!visible}
      >
        <WorksUsersSelect
          size="large"
          worksUsers={worksUsers}
          disabled={!canUpdateDetails || enableOnlyOpenPositionsField}
        />
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: 'Please enter a description' }]}
        hidden={!visible}
      >
        <TextArea
          placeholder="Please provide more details..."
          maxLength={1000}
          disabled={!canUpdateDetails || enableOnlyOpenPositionsField}
        />
      </Form.Item>

      <Form.Item label="External Job ID" name="externalId" hidden={!visible}>
        <Input size="large" disabled={enableOnlyOpenPositionsField} />
      </Form.Item>
    </>
  );
}
